import React, { useState, useEffect, useCallback } from "react";
import { Select, Input, Button, Space, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { API_CONST } from "../const";

const { Option } = Select;

const OTHER_VALUE = "__other__";

const RoleSelectWithOther = ({
  value,
  onChange,
  source,
  placeholder = "Select Role",
  disabled = false,
  size,
  style,
  onAfterChange,
}) => {
  const [roles, setRoles] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customRole, setCustomRole] = useState("");
  const [addingCustom, setAddingCustom] = useState(false);

  const fetchRoles = useCallback(async () => {
    setFetchLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.ROLE_PRICING_ROLE_NAMES, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRoles(data.roles || []);
      }
    } catch {
    } finally {
      setFetchLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleChange = (val) => {
    if (val === OTHER_VALUE) {
      setShowCustomInput(true);
      setCustomRole("");
      return;
    }
    setShowCustomInput(false);
    onChange?.(val);
    onAfterChange?.();
  };

  const handleAddCustomRole = async () => {
    const trimmed = customRole.trim();
    if (!trimmed) {
      message.warning("Please enter a role name");
      return;
    }
    setAddingCustom(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API_CONST.ROLE_PRICING_CUSTOM_ROLE, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ roleName: trimmed, source: source || "customer" }),
      });
      const data = await response.json();
      if (response.ok) {
        const finalName = data.roleName || trimmed;
        if (!roles.includes(finalName)) {
          setRoles((prev) => [...prev, finalName].sort());
        }
        setShowCustomInput(false);
        setCustomRole("");
        onChange?.(finalName);
        onAfterChange?.();
        if (!data.existed) {
          message.success(`"${finalName}" added as a new role`);
        }
      } else {
        message.error(data.message || "Failed to add custom role");
      }
    } catch {
      message.error("Failed to add custom role");
    } finally {
      setAddingCustom(false);
    }
  };

  const handleCancelCustom = () => {
    setShowCustomInput(false);
    setCustomRole("");
  };

  const displayValue = showCustomInput ? OTHER_VALUE : value;

  return (
    <div>
      <Select
        placeholder={placeholder}
        showSearch
        loading={fetchLoading}
        optionFilterProp="children"
        filterOption={(input, option) =>
          option?.children
            ? String(option.children).toLowerCase().includes(input.toLowerCase())
            : false
        }
        value={displayValue || undefined}
        onChange={handleChange}
        disabled={disabled}
        size={size}
        style={style || { width: "100%" }}
        allowClear={!showCustomInput}
        onClear={() => { onChange?.(undefined); onAfterChange?.(); }}
      >
        {roles.map((role) => (
          <Option key={role} value={role}>
            {role}
          </Option>
        ))}
        <Option value={OTHER_VALUE} style={{ color: "#00b896", fontStyle: "italic" }}>
          + Other (add custom role)
        </Option>
      </Select>

      {showCustomInput && (
        <Space.Compact style={{ width: "100%", marginTop: 8 }}>
          <Input
            placeholder="Type custom role name..."
            value={customRole}
            onChange={(e) => setCustomRole(e.target.value)}
            onPressEnter={handleAddCustomRole}
            autoFocus
          />
          <Button
            type="primary"
            loading={addingCustom}
            onClick={handleAddCustomRole}
            icon={<PlusOutlined />}
            style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
          >
            Add
          </Button>
          <Button onClick={handleCancelCustom} disabled={addingCustom}>
            Cancel
          </Button>
        </Space.Compact>
      )}
    </div>
  );
};

export default RoleSelectWithOther;
